import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';

import * as http from '../utils/http';
import history from '../utils/history';
import { capitalizeFirstLetter, checkIfHotjarEnable, timeBetweenDates } from '../utils/helpers';
import { coveragePlans } from '../data/coveragePlan';
import * as sinisterCardsActions from '../redux/actions/sinister-cards';
import FilesUploadingProps from '../data/filesUploadingProps';
import FileAttachComp from '../components/content/FileAttachComp/FileAttachComp';
import InfoCard from '../components/content/InfoCard/InfoCard';
import RimacTag from '../components/common/RimacTag/RimacTag';
import SplitLayout from '../layout/SplitLayout';
import IconInfo from '../assets/infoIcon.svg';
import { PAGINAS_BY_STEP } from '../utils/constants';

const FilesAttachPage = () => {
  const [viewStartTime] = useState(new Date());
  const dispatch = useDispatch();

  const registerNum = useSelector((state) => state.sinisterCards.sinisterNumberCase);
  const sinisterDate = useSelector((state) => state.sinisterCards.sinisterDate);
  const userFlowType = useSelector((state) => state.coverages.userFlowType);
  const currentCoveragePlan = useSelector(
    (state) => state.coverages.currentPlan
  );
  const currentUser = useSelector((state) => state.sinisterCards.userInfo);
  const currentUserName = `${capitalizeFirstLetter(
    currentUser?.name ?? ''
  )} ${capitalizeFirstLetter(currentUser?.fatherLastName ?? '')}`;
  const { listFiles } = FilesUploadingProps;
  const sinisterList = useSelector((state) => state.sinisterCards.sinisterList);
  const sinisterFileList = useSelector(
    (state) => state.sinisterCards.sinisterFiles
  );
  const [userWait, setUserWait] = useState(false);
  const [requiredDocuments, setRequiredDocuments] = useState(listFiles.filter((file) => file.id !== '6'));
  const [isThirdServiceSelected] = useState(sinisterList?.find(
    (item) => item.code === 'COB03'
  ));

  useEffect(() => {
    if (!viewStartTime) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

    const endTime = new Date();
    const userTotalTime = timeBetweenDates(viewStartTime, endTime);

    if (checkIfHotjarEnable()) {
      // LAST
      window.digitalData.push({
        task: {
          name: 'Tiempo registrado - Docuementos Requeridos',
          time: userTotalTime,
        },
        event: 'trackTimeToComplete',
      });
    }
  }, [viewStartTime]);

  useEffect(() => {
    if ([isThirdServiceSelected, listFiles, sinisterList, userFlowType].includes(''))  return
    const isSecondServiceSelected = sinisterList.find(
      (item) => item.code === 'COB02'
    );
    if (isSecondServiceSelected && sinisterList.length === 1) {
      setRequiredDocuments(requiredDocuments.slice(0,-1))
    }

    if (isThirdServiceSelected) {
      const carta = listFiles.find((type) => type.id === '6')
      setRequiredDocuments([...requiredDocuments, carta])
    }
    if (userFlowType === 'attach') {
      setRequiredDocuments(listFiles.filter((item) => item.id === '6'));
    }
  }, [isThirdServiceSelected, listFiles, sinisterList, userFlowType]);

  const isNextActive = () => {
    if (isThirdServiceSelected) {
      const requiredFilesWithoutBBVA = sinisterFileList.filter(
        (item) => item.typeFile !== 'CARTA DEL BANCO'
      );
      const allHaveFiles = requiredFilesWithoutBBVA.every(
        (item) => item.files.length > 0
      );
      if (allHaveFiles && requiredFilesWithoutBBVA.length >= 5) {
        return true;
      }
    }

    if (sinisterFileList.length < requiredDocuments.length) {
      return false;
    }

    for (const item of sinisterFileList) {
      if (item.files <= 0) {
        return false;
      }
    }

    return true;
  };

  const onBackHandler = () => {
    dispatch(sinisterCardsActions.reduceStep({plan: currentCoveragePlan}));
    if (userFlowType === 'attach') {
      history.replace(`/`);
      return;
    }

    history.replace(`/${coveragePlans[currentCoveragePlan].uri}/antes-de-comenzar`);
  };

  const onContinueHandler = async () => {
    if (userFlowType === 'attach') {
      setUserWait(true);
      const realDate = new Date(sinisterDate);
      const month = String(realDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(realDate.getUTCDate()).padStart(2, '0');
      const year = realDate.getUTCFullYear();
      const fechaSiniestro = `${day}/${month}/${year}`;

      const subTipoDocumentos = [];
      try {
        sinisterFileList.forEach((sFile) => {
          sFile.files.forEach((file) => {
            subTipoDocumentos.push({
              tipo: sFile.typeFile,
              extension: String(file.name.split('.').pop()).toLowerCase(),
              content: file,
            });
          });
        });
        const subTipoDsocumentosInfo = subTipoDocumentos.map((file) => {
          return {
            tipo: file['tipo'],
            extension: file['extension'],
          };
        });

        const docPayload = {
          data: {
            codigoCaso: registerNum,
            aseguradoDetalle: {
              tipoDocumento: currentUser.documentType.value,
              numeroDocumento: currentUser.document,
            },
            subTipoDocumentos: subTipoDsocumentosInfo,
            fechaSiniestro,
          },
        };

        const docs = await http.post(
          '/documentos',
          docPayload,
          process.env.REACT_APP_API_NAME
        );
        await Promise.all(
          docs.payload.documentos.map(async (el, i) => {
            let blob = await fetch(subTipoDocumentos[i].content.content).then(
              (r) => r.blob()
            );
            const newFile = new File([blob], subTipoDocumentos[i].content.name);
            await axios.put(el.url, newFile, {
              headers: {
                'Content-Type': subTipoDocumentos[i].content.type,
              },
            });
          })
        );

        const sinisterPayload = {
          siniestro: {
            codigoCaso: registerNum,
            persona: {
              tipoDocumento: currentUser.documentType.value,
              numeroDocumento: currentUser.document,
            },
            fechaSiniestro,
            documento: {
              subtipo: requiredDocuments[0].typeFile
            }
          }
        };
        const result = await http.patch(
          '/siniestros',
          sinisterPayload,
          process.env.REACT_APP_API_NAME
        );
        if (result.payload?.status !== 1) {
          history.replace('/new-error');
          return;
        }
      } catch (error) {
        console.log('error', error);
        history.replace('/new-error');
        return;
      }
      dispatch(sinisterCardsActions.resetState());
      dispatch(sinisterCardsActions.setStep(PAGINAS_BY_STEP.NEW_THANKYOU.step));
      history.replace('/new-gracias?type=postCartaBanco');
      setUserWait(false);
    } else if (userFlowType === 'input') {
      dispatch(sinisterCardsActions.incrementStep());
      history.replace(`/${coveragePlans[currentCoveragePlan].uri}/montos-robados`);
      setActionDropFile(sinisterFileList.map(file=>file.title).join(' - '), "Continuar");
    } else {
      history.replace(`/${coveragePlans[currentCoveragePlan].uri}/resumen`);
    }
  };

  const onShowExample = (title) => {
    setActionDropFile(title, "Ver ejemplo");
  };
  const onDeleteFile = (title) => {
    setActionDropFile(title, "Eliminar");
  };

  const onLoadFile = (title) => {
    setActionDropFile(title, "Haz click aquí");
    
  };

  const setActionDropFile = (title, action) => {
    if (checkIfHotjarEnable()) {
      window.digitalData.push ({
        "action":{
            "group":"Tramites Siniestros - Protección Tarjeta BBVA",
            "category":`Adjunta documentos - ${capitalizeFirstLetter(currentCoveragePlan.toLowerCase())}`,
            "name": action,
            "label": title
        },
        "event":"trackAction"
    });
    }
  };

  const leftContent = (
    <>
      <RimacTag
        customClasses="u-mb-8"
        color={coveragePlans[currentCoveragePlan].color}
        text={coveragePlans[currentCoveragePlan].name}
      />
      <div className="titleCont">
        <h1 className="u-mb-40">Seguro de Protección de Tarjetas</h1>
        {userFlowType !== 'attach' && (
          <p>
            <span className="u-font-bold">{currentUserName}</span>, estás
            solicitando:
          </p>
        )}
        <ul className="rimacCardCont">
          {sinisterList.map((cov) => (
            <div key={cov.code} className="rimacLiteCard u-mb-8">
              <img
                className="rimacLiteCard__img"
                src={cov.icon}
                alt={cov.title}
              />
              <p className="rimacLiteCard__title">{cov.title}</p>
            </div>
          ))}
        </ul>
      </div>
    </>
  );

  const rightContent = (
    <>
      <RimacTag
        customClasses="u-mb-16 mobile"
        color={coveragePlans[currentCoveragePlan].color}
        text={coveragePlans[currentCoveragePlan].name}
      />
      <div className="titleCont">
        <h2 className="u-mb-16">Adjunta los siguientes documentos</h2>

        <InfoCard
          icon={IconInfo}
          customClasses={'u-mb-40'}
          content={
            <p>
              <b>Recuerda:</b><br/>Si tienes dudas sobre algún documento, puedes ver
              los ejemplos.
            </p>
          }
        />

        <div className="mainContent">
          {requiredDocuments &&
            requiredDocuments.map((resp, i) => {
              return (
                <div
                  data-testid={'attach-input-' + i}
                  key={'up-file-' + resp.title}
                  className="u-mb-40-32"
                >
                  <FileAttachComp
                    onShowExample={onShowExample}
                    onLoadFile={onLoadFile}
                    onDeleteFile={onDeleteFile}
                    title={resp.title}
                    customClass={
                      i < requiredDocuments.length - 1 ? 'borderBotResp' : ''
                    }
                    description={resp.description}
                    docUrl={resp.docUrl}
                    typeFile={resp.typeFile}
                    typeFileId={resp.id}
                    currentFiles={sinisterFileList.find(
                      (item) => item.id === resp.id
                    )}
                    maxFiles={userFlowType === 'attach' ? 1 : 2}
                  />
                </div>
              );
            })}
        </div>
      </div>
    </>
  );

  return (
    <SplitLayout
      dataTestId='files-attach-page'
      containerClasses="FilesAttachPage"
      leftClasses="FilesAttachPage__left"
      rightClasses="FilesAttachPage__right"
      leftContent={leftContent}
      rightContent={rightContent}
      isNavActive={userFlowType === 'input' || userFlowType === 'attach'}
      isNavNextDisabled={!isNextActive()}
      onNavBackClick={onBackHandler}
      onNavContinueClick={onContinueHandler}
      navCurrentStep={3}
      navTotalSteps={5}
      navStepperText={userFlowType === 'attach' ? 'VOLVER' : 'PASO 3 DE 5'}
      navBtnText="Continuar"
      isNavLoading={userWait}
    ></SplitLayout>
  );
};

export default FilesAttachPage;
